<template>
  <div class="min-h-screen bg-white">

    <!-- Hero Banner -->
    <section class="relative overflow-hidden bg-blog-beige border-b border-blog-gold" style="min-height: 260px;">
      <div class="flex items-center justify-center w-64 h-64 absolute circle-rotating-logo opacity-20 pointer-events-none" style="right: 5%; top: 50%; transform: translateY(-50%);">
        <div class="absolute w-full h-full animate-spin-slow">
          <svg viewBox="0 0 100 100" class="w-full h-full">
            <defs>
              <path id="circlePath-pp" d="M 50, 50 m -37, 0 a 37,37 0 1,1 74,0 a 37,37 0 1,1 -74,0" />
            </defs>
            <text class="f-font-times-new-roman-spaced fill-blog-red text-[1rem] uppercase">
              <textPath href="#circlePath-pp">
                Teacher Maestra Victoria  Teacher Maestra Victoria  Teacher Maestra Victoria 
              </textPath>
            </text>
          </svg>
        </div>
      </div>

      <div class="container mx-auto px-8 md:px-20 py-16 relative z-10">
        <p class="text-sm uppercase tracking-[0.25em] font-bold text-blog-old-red/60 mb-3 f-font-helvetica">
          {{ translate('legal') }}
        </p>
        <h1 class="f-font-times-new-roman text-4xl md:text-6xl text-blog-old-red mb-4">
          {{ translate('Privacy Policy') }}
        </h1>
        <p class="f-font-helvetica text-blog-old-red/70 text-sm">
          {{ translate('Last updated') }}: {{ lastUpdated }}
        </p>
      </div>
    </section>

    <!-- Content -->
    <main class="px-8 md:px-20 mx-auto py-16 max-w-[900px]">

      <!-- Intro card -->
      <div class="bg-blog-beige border border-blog-gold rounded-[20px] p-8 mb-12">
        <p class="f-font-helvetica text-blog-old-red/80 text-base leading-relaxed mb-0">
          {{ translate('The') }} <strong class="text-blog-old-red">{{ translate('Escola de Idiomas Teacher Maestra Victoria') }}</strong>
          {{ translate('values your privacy. This document explains what data we collect, how we use it, and what your rights are, in compliance with the') }}
          <strong class="text-blog-old-red">{{ translate('General Data Protection Law (LGPD - Law No. 13,709/2018)') }}</strong>.
          {{ translate('By using the platform, you agree to the terms of this Policy.') }}
        </p>
      </div>

      <!-- Sections -->
      <div class="space-y-12">

        <PolicySection :number="1" :title="translate('Who we are')">
          <p>
            {{ translate('We are an online language school offering recorded English courses, a streaming platform for purchased content, and a private lesson management tool - exclusive materials and management.') }}
          </p>
          <p class="text-sm text-blog-old-red/60 mt-2 f-font-helvetica">
            CNPJ: {{ context.companyCNPJ }}
          </p>
        </PolicySection>

        <PolicySection :number="2" :title="translate('Data we collect')">
          <p class="mb-4">{{ translate('We may collect the following categories of personal data:') }}</p>
          <ul class="space-y-3">
            <PolicyItem icon="pi-user">
              <strong>{{ translate('Registration data:') }}</strong> {{ translate('name, email, phone, and where applicable, CPF.') }}
            </PolicyItem>
            <PolicyItem icon="pi-globe">
              <strong>{{ translate('Access data:') }}</strong> {{ translate('IP address, browser type, operating system, and browsing history on the platform.') }}
            </PolicyItem>
            <PolicyItem icon="pi-credit-card">
              <strong>{{ translate('Payment data:') }}</strong> {{ translate('processed by secure third-party gateways. We do not store card data on our servers.') }}
            </PolicyItem>
            <PolicyItem icon="pi-book">
              <strong>{{ translate('Progress data:') }}</strong> {{ translate('purchased courses, watched lessons, and exclusive materials accessed.') }}
            </PolicyItem>
            <PolicyItem icon="pi-comments">
              <strong>{{ translate('Communications:') }}</strong> {{ translate('messages exchanged with platform support.') }}
            </PolicyItem>
          </ul>
        </PolicySection>

        <PolicySection :number="3" :title="translate('Cookies and similar technologies')">
          <p class="mb-6">{{ translate('We use the following cookies on the platform:') }}</p>
          <div class="space-y-4">
            <CookieCard
              name="TM_TOKEN"
              :badge="translate('Essential')"
              badgeClass="bg-blog-red/10 text-blog-old-red"
            >
              {{ translate('Essential session cookie. Stores the authentication token (JWT) required to keep you logged in and control access to your account and purchased content.') }}
              <strong>{{ translate('This cookie is strictly necessary for the platform to function and cannot be disabled.') }}</strong>
            </CookieCard>
          </div>
          <p class="mt-6 text-sm text-blog-old-red/60 f-font-helvetica">
            {{ translate('We do not use advertising tracking cookies, third-party cookies for marketing purposes, or intrusive behavioral analysis tools.') }}
          </p>
        </PolicySection>

        <PolicySection :number="4" :title="translate('Purpose of data use')">
          <ul class="space-y-3">
            <PolicyItem icon="pi-check-circle" v-for="(item, i) in purposes" :key="i">{{ translate(item) }}</PolicyItem>
          </ul>
        </PolicySection>

        <PolicySection :number="5" :title="translate('Legal basis for processing')">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <LegalBasisCard
              v-for="(basis, i) in legalBases" :key="i"
              :title="translate(basis.title)"
              :description="translate(basis.description)"
            />
          </div>
        </PolicySection>

        <PolicySection :number="6" :title="translate('Data sharing')">
          <p class="mb-4">
            {{ translate('We do not sell, rent, or share your personal data with third parties for commercial purposes. We may share data only in the following situations:') }}
          </p>
          <ul class="space-y-3">
            <PolicyItem icon="pi-credit-card">{{ translate('With payment processors, to complete financial transactions.') }}</PolicyItem>
            <PolicyItem icon="pi-server">{{ translate('With hosting and infrastructure providers, under confidentiality agreements.') }}</PolicyItem>
            <PolicyItem icon="pi-shield">{{ translate('With public authorities, when required by law or court order.') }}</PolicyItem>
          </ul>
        </PolicySection>

        <PolicySection :number="7" :title="translate('Storage and security')">
          <p>
            {{ translate('Your data is stored on secure servers. We adopt technical and administrative measures to protect your data against unauthorized access, loss, alteration, or improper disclosure. The authentication token (cookie') }}
            <code class="bg-blog-beige border border-blog-gold px-1.5 py-0.5 rounded text-sm text-blog-old-red font-mono">TM_TOKEN</code>
            {{ translate(') is generated with an expiration date and automatically invalidated when the session ends.') }}
          </p>
        </PolicySection>

        <PolicySection :number="8" :title="translate('Your rights (LGPD)')">
          <p class="mb-4">{{ translate('Under the LGPD, you have the right to:') }}</p>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div
              v-for="(right, i) in rights" :key="i"
              class="flex items-start gap-3 bg-blog-beige border border-blog-gold rounded-2xl p-4"
            >
              <i class="pi pi-check text-blog-old-red mt-0.5 shrink-0"></i>
              <span class="text-sm text-blog-old-red/80 f-font-helvetica">{{ translate(right) }}</span>
            </div>
          </div>
        </PolicySection>

        <PolicySection :number="9" :title="translate('Changes to this policy')">
          <p>
            {{ translate('We may update this Policy periodically. When significant changes occur, we will notify you by email or platform notice. The "last updated" date at the top of this document indicates when the current version was published.') }}
          </p>
        </PolicySection>

        <!-- Contact -->
        <div class="bg-blog-red rounded-[28px] p-8 text-white text-center border-2 border-blog-gold">
          <h2 class="f-font-times-new-roman italic text-2xl md:text-3xl mb-4">
            {{ translate('Contact us') }}
          </h2>
          <p class="f-font-helvetica font-light mb-2">{{ context.companyName }}</p>
          <p class="f-font-helvetica font-light text-white/70 mb-4">CNPJ: {{ context.companyCNPJ }}</p>
          <a
            :href="`mailto:${context.supportEmail}`"
            class="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 transition-colors border border-white/30 text-white px-6 py-3 rounded-full text-sm font-bold tracking-wide"
          >
            <i class="pi pi-envelope"></i>
            {{ context.supportEmail }}
          </a>
        </div>

      </div>
    </main>
  </div>
</template>

<script setup>
import { translate } from '@/composables/translate';
import { useContextStore } from '@/stores/context';

const context = useContextStore();
const lastUpdated = translate('july 2026');

const purposes = [
  'Create and manage your account',
  'Grant access to purchased courses and materials',
  'Register and manage private students',
  'Process payments and issue receipts',
  'Send communications related to your learning',
  'Ensure the security and proper functioning of the platform',
  'Comply with legal and regulatory obligations',
];

const legalBases = [
  { title: 'Contract performance', description: 'For providing purchased courses and services.' },
  { title: 'Consent', description: 'For sending marketing communications, when applicable.' },
  { title: 'Legitimate interest', description: 'For platform security and service improvement.' },
  { title: 'Legal obligation', description: 'When required by law or competent authority.' },
];

const rights = [
  'Confirm whether we process your personal data',
  'Access the data we hold about you',
  'Correct incomplete or outdated data',
  'Request anonymization, blocking, or deletion',
  'Withdraw consent at any time',
  'Request data portability',
  'Obtain information about third-party sharing',
];
</script>

<!-- Sub-componentes inline -->
<script>
import { defineComponent, h } from 'vue';

export const PolicySection = defineComponent({
  props: { number: Number, title: String },
  setup(props, { slots }) {
    return () => h('section', { class: 'border-t border-blog-gold pt-10' }, [
      h('div', { class: 'flex items-baseline gap-4 mb-6' }, [
        h('span', {
          class: 'f-font-times-new-roman text-5xl text-blog-old-red/20 leading-none select-none'
        }, String(props.number).padStart(2, '0')),
        h('h2', {
          class: 'f-font-times-new-roman text-2xl md:text-3xl text-blog-old-red'
        }, props.title),
      ]),
      h('div', { class: 'f-font-helvetica text-blog-old-red/80 leading-relaxed text-base' }, slots.default?.()),
    ]);
  }
});

export const PolicyItem = defineComponent({
  props: { icon: { type: String, default: 'pi-circle' } },
  setup(props, { slots }) {
    return () => h('li', { class: 'flex items-start gap-3' }, [
      h('i', { class: `pi ${props.icon} text-blog-old-red mt-1 shrink-0 text-sm` }),
      h('span', { class: 'text-blog-old-red/80 text-sm leading-relaxed' }, slots.default?.()),
    ]);
  }
});

export const CookieCard = defineComponent({
  props: { name: String, badge: String, badgeClass: String },
  setup(props, { slots }) {
    return () => h('div', {
      class: 'bg-[#fffaf4] border border-blog-gold rounded-2xl p-5'
    }, [
      h('div', { class: 'flex items-center gap-3 mb-2' }, [
        h('code', {
          class: 'font-mono text-sm font-bold text-blog-old-red bg-blog-beige border border-blog-gold px-2 py-0.5 rounded-lg'
        }, props.name),
        h('span', {
          class: `text-xs font-bold uppercase tracking-wide px-2.5 py-0.5 rounded-full ${props.badgeClass}`
        }, props.badge),
      ]),
      h('p', { class: 'text-sm text-blog-old-red/70 f-font-helvetica leading-relaxed' }, slots.default?.()),
    ]);
  }
});

export const LegalBasisCard = defineComponent({
  props: { title: String, description: String },
  setup(props) {
    return () => h('div', {
      class: 'bg-blog-beige border border-blog-gold rounded-2xl p-5'
    }, [
      h('h3', { class: 'f-font-times-new-roman text-blog-old-red font-bold text-base mb-1' }, props.title),
      h('p', { class: 'text-sm text-blog-old-red/70 f-font-helvetica' }, props.description),
    ]);
  }
});
</script>